import { useEffect, useLayoutEffect, useRef } from 'react'
import type { ChartManager } from '../core/ChartManager'
import type { OverlaySyncGroup, OverlaySyncEvent } from '../utils/overlaySync'
import type { SavedDrawing } from '../types/persistence'

type Chart = ReturnType<ChartManager['getChart']>

/**
 * Subscribes the chart to an overlay sync group (multi-chart drawing sync)
 * and handles the single-panel fallback. Also restores saved drawings on
 * initial load.
 *
 * Merges the logic from three EdgeChart useEffects:
 * - Overlay sync group subscription (L339-357)
 * - Single-panel overlay event passthrough (L360-373)
 * - Drawing restore on chart ready (L377-403)
 */

export function useOverlaySync(
  managerRef: React.RefObject<ChartManager | null>,
  overlaySyncGroup: OverlaySyncGroup | undefined,
  chartId: string | undefined,
  ticker: string,
  onDrawingEvent: ((ticker: string, event: OverlaySyncEvent) => void) | undefined,
): void {
  useEffect(() => {
    const manager = managerRef.current
    if (!manager || !overlaySyncGroup || !chartId) return

    manager.onOverlaySyncEvent((event) => {
      overlaySyncGroup.publish(chartId, ticker, event)
      onDrawingEvent?.(ticker, event)
    })

    overlaySyncGroup.subscribe(chartId, ticker, (event) => {
      manager.applyOverlaySync(event)
    })

    return () => {
      manager.onOverlaySyncEvent(null)
      overlaySyncGroup.unsubscribe(chartId)
    }
  }, [managerRef, overlaySyncGroup, chartId, ticker, onDrawingEvent])

  // Single-panel fallback — fire onDrawingEvent when no overlaySyncGroup
  useEffect(() => {
    const manager = managerRef.current
    if (!manager || overlaySyncGroup) return
    if (!onDrawingEvent) return

    manager.onOverlaySyncEvent((event) => {
      onDrawingEvent(ticker, event)
    })

    return () => {
      manager.onOverlaySyncEvent(null)
    }
  }, [managerRef, overlaySyncGroup, ticker, onDrawingEvent])
}

/**
 * Restores saved drawings once the chart is ready (has data loaded).
 * Only runs on initial load — subsequent drawing changes come through
 * overlay sync events, not this restore path.
 *
 * Accepts chartInstance as the trigger — when it changes from null to a Chart,
 * the effect fires and accesses the EventBus from managerRef.
 */
/** How long chart:ready may trail the bars before it counts as missed. */
const READY_MISSED_AFTER_MS = 5_000

export function useDrawingRestore(
  managerRef: React.RefObject<ChartManager | null>,
  chartInstance: Chart | null,
  effectiveDrawings: SavedDrawing[] | undefined,
  drawingsRestoredRef: React.MutableRefObject<boolean>,
  /**
   * Diagnostic: the drawings were restored by the bars-landed fallback and
   * chart:ready never followed. On a normal load the fallback also runs (bars
   * land just before ready), so only a ready that never arrives is reported.
   */
  onReadyMissed?: () => void,
): void {
  const onReadyMissedRef = useRef(onReadyMissed)
  useLayoutEffect(() => {
    onReadyMissedRef.current = onReadyMissed
  })

  useEffect(() => {
    const manager = managerRef.current
    if (!manager || !chartInstance || effectiveDrawings === undefined) return
    if (effectiveDrawings.length === 0) return

    const restore = () => {
      // Deliberately NOT one-shot. This used to latch after the first restore,
      // so a drawing added later never reached a chart that had already loaded
      // — which is why a line drawn in one grid cell never appeared in its
      // siblings, even though drawings are stored per TICKER and all cells
      // share one store.
      //
      // Re-running is safe: `applyOverlaySync` is idempotent per syncId (it
      // returns early when the id is already mapped to a live overlay), so
      // existing drawings are skipped and only genuinely new ones are created.
      drawingsRestoredRef.current = true
      for (const drawing of effectiveDrawings) {
        manager.applyOverlaySync({
          action: 'create',
          syncId: drawing.syncId,
          name: drawing.name,
          points: drawing.points,
          // Restore persisted style / visibility / lock / extendData
          // (all undefined on legacy drawings).
          styles: drawing.styles,
          extendData: drawing.extendData,
          visible: drawing.visible,
          lock: drawing.lock,
        })
      }
    }

    // In-place symbol swap: ChartManager.setSymbol removes every live overlay
    // and resets the sync maps AFTER this effect has already re-applied the
    // returning ticker's set (EdgeChart mounts this hook before its setSymbol
    // effect, so within one commit restore ran first and the cleanup deleted
    // its work — the drawings then stayed gone until a full page refresh).
    // Re-applying on the cleared-slate event closes that hole in BOTH effect
    // orders: applyOverlaySync is idempotent per syncId, so a re-apply on an
    // untouched chart creates nothing.
    const offCleared = manager.getEventBus().on('overlays:cleared', restore)

    // Wait for chart data before restoring — overlays need data for positioning
    let offReady: (() => void) | undefined
    let offData: (() => void) | undefined
    let missedTimer: ReturnType<typeof setTimeout> | undefined
    let missedArmed = false
    if (chartInstance.getDataList().length > 0) {
      restore()
    } else {
      offReady = manager.getEventBus().on('chart:ready', () => {
        clearTimeout(missedTimer)
        restore()
      })
      // chart:ready alone is not a safe thing to park on. It is one-shot
      // bookkeeping, and it has been missed in the field: a settings reload that
      // superseded the first load landed the bars without it ever firing, and a
      // first page with ZERO bars fires it before there is anything to wait
      // for. `data:loaded` is emitted by every path that lands bars (first
      // page, reload, preloaded period, scroll-back), so a cell can no longer
      // be stranded without its drawings. Re-running is free: restore is
      // idempotent per syncId.
      const onBars = () => {
        if (chartInstance.getDataList().length === 0) return
        restore()
        if (missedArmed) return
        missedArmed = true
        missedTimer = setTimeout(() => onReadyMissedRef.current?.(), READY_MISSED_AFTER_MS)
      }
      const offLoaded = manager.getEventBus().on('data:loaded', onBars)
      // A chart whose first page was empty gets its first bar from the stream,
      // which goes through updateData and emits data:updated, not data:loaded.
      const offUpdated = manager.getEventBus().on('data:updated', onBars)
      offData = () => {
        offLoaded()
        offUpdated()
      }
    }
    return () => {
      offCleared()
      offReady?.()
      offData?.()
      clearTimeout(missedTimer)
    }
  }, [managerRef, effectiveDrawings, chartInstance, drawingsRestoredRef])
}
